import React, { useState, useEffect, useRef } from 'react';
import { LogOut, Bell, BellDot, CheckCircle2, ChevronRight, Moon, Sun, User as UserIcon, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';

const Topbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const dropdownRef = useRef(null);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    if (isDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    if (user) fetchInvites();
  }, [user, location.pathname]);

  const fetchInvites = async () => {
    try {
      const { data } = await api.get('/invitations');
      setInvitations(data);
    } catch (err) { console.error("Error invites"); }
  };

  const unreadCount = invitations.filter(inv => !inv.is_read).length;

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white/90 dark:bg-[#0b0f1a]/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 z-50 flex items-center justify-between px-4 sm:px-6 md:px-8 gap-3 transition-colors">
      <div className="flex items-center gap-3 sm:gap-6 min-w-0">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 rounded-xl transition-colors md:hidden shrink-0"
          aria-label="Abrir menu"
        >
          <Menu size={22} />
        </button>

        <h1
          onClick={() => navigate('/dashboard')}
          className="text-base sm:text-xl font-bold text-blue-600 tracking-tight whitespace-nowrap shrink-0 cursor-pointer select-none"
          title="Ir para a página inicial"
        >
          PRINT<span className="text-slate-900 dark:text-white transition-colors">HERITAGE</span>
        </h1>

        <div className="relative" ref={dropdownRef}>
          <button onClick={() => setShowDropdown(!showDropdown)} className="p-2 text-slate-400 hover:text-blue-600 transition-all rounded-xl relative">
            {unreadCount > 0 ? <BellDot size={22} className="text-blue-600" /> : <Bell size={22} />}
            {unreadCount > 0 && <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-[#0b0f1a] animate-pulse"></span>}
          </button>

          {showDropdown && (
            <div className="fixed left-4 right-4 top-16 sm:absolute sm:left-0 sm:right-auto sm:top-auto sm:mt-3 sm:w-80 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2">
              <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-950/50">
                <span className="font-black text-[10px] uppercase text-slate-500 dark:text-slate-400 tracking-widest">Notificações</span>
                {unreadCount > 0 && <span className="text-[9px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-black uppercase tracking-tighter">{unreadCount} Pendentes</span>}
              </div>
              <div className="max-h-80 overflow-y-auto custom-scrollbar">
                {invitations.length === 0 ? <div className="p-10 text-center text-slate-400 text-xs italic">Sem notificações novas.</div> : 
                  invitations.map(inv => (
                    <div key={inv.id} onClick={() => { navigate('/notifications'); setShowDropdown(false); }} className={`p-4 border-b border-slate-50 dark:border-slate-800 cursor-pointer transition-colors flex space-x-3 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 ${!inv.is_read ? 'bg-blue-50/30 dark:bg-blue-900/5' : ''}`}>
                      <div className="mt-1 shrink-0"><CheckCircle2 size={16} className="text-blue-500"/></div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-relaxed">
                          <span className="font-bold text-slate-900 dark:text-slate-200">{inv.inviter_email}</span> convidou-te para colaborar em <span className="font-bold text-slate-900 dark:text-slate-200">{inv.project_name}</span>.
                        </p>
                        <p className="text-[9px] text-blue-500 font-black uppercase mt-1 tracking-tighter">{inv.access_level}</p>
                      </div>
                    </div>
                  ))
                }
              </div>
              <button onClick={() => { navigate('/notifications'); setShowDropdown(false); }} className="w-full p-4 bg-slate-50 dark:bg-slate-950/50 hover:bg-blue-600 hover:text-white text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest flex items-center justify-center space-x-2 transition-all border-t border-slate-100 dark:border-slate-800">
                <span>Ver Todos os Convites</span> <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-4 shrink-0">
        <button onClick={() => setIsDark(!isDark)} className="p-2.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-yellow-400 rounded-xl transition-all border border-slate-200 dark:border-slate-700">
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="hidden sm:flex flex-col items-end border-l border-slate-200 dark:border-slate-800 pl-4 max-w-[160px] md:max-w-[220px]">
          <span className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate max-w-full">{user?.email}</span>
          <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{user?.global_role}</span>
        </div>

        <button onClick={logout} className="p-2.5 text-slate-400 hover:text-red-500 transition-colors">
          <LogOut size={20} />
        </button>
      </div>
    </header>
  );
};

export default Topbar;
